import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl } from "../utils/Constants";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: token,
    },
  };
};

export const fetchAIAnswer = createAsyncThunk(
  "aiAnswer/fetchAIAnswer",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/questions/${questionId}/ai-answer`,
        getAuthHeader(),
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch AI answer",
      );
    }
  },
);

export const askAI = createAsyncThunk(
  "aiAnswer/askAI",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/questions/${questionId}/ask-ai`,
        {},
        getAuthHeader(),
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Failed to generate AI answer",
        code: error.response?.data?.code || null,
        status: error.response?.status || null,
        aiAnswer: error.response?.data?.aiAnswer || null,
      });
    }
  },
);

export const submitAIFeedback = createAsyncThunk(
  "aiAnswer/feedback",
  async ({ questionId, helpful }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/questions/${questionId}/ai-answer/feedback`,
        { helpful },
        getAuthHeader(),
      );

      return {
        ...response.data,
        helpful,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save AI feedback",
      );
    }
  },
);

export const refineAIAnswer = createAsyncThunk(
  "aiAnswer/refine",
  async ({ questionId, action }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/questions/${questionId}/ai-answer/refine`,
        { action },
        getAuthHeader(),
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to generate another AI response",
      );
    }
  },
);

export const publishAIAnswer = createAsyncThunk(
  "aiAnswer/publish",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/questions/${questionId}/ai-answer/publish`,
        {},
        getAuthHeader(),
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to publish AI answer",
      );
    }
  },
);

export const discardAIAnswer = createAsyncThunk(
  "aiAnswer/discard",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/questions/${questionId}/ai-answer/discard`,
        {},
        getAuthHeader(),
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to discard AI answer",
      );
    }
  },
);

const initialState = {
  status: "none", // none | draft | published
  aiAnswerId: null,
  aiAnswer: null,
  isHelpful: null,
  createdAt: null,
  expiresAt: null,
  publishedAt: null,
  publishedAnswer: null,
  loading: false,
  refining: false,
  publishing: false,
  discarding: false,
  feedbackLoading: false,
  error: null,
  errorCode: null,
  humanAnswerExists: false,
};

const aiAnswerSlice = createSlice({
  name: "aiAnswer",
  initialState,
  reducers: {
    clearAIAnswer: (state) => {
      Object.assign(state, initialState);
    },
    clearAIError: (state) => {
      state.error = null;
      state.errorCode = null;
      state.humanAnswerExists = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchAIAnswer.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.errorCode = null;

        const data = action.payload || {};

        state.status = data.status || "none";
        state.aiAnswerId = data.aiAnswerId || null;
        state.aiAnswer = data.aiAnswer || null;
        state.isHelpful = data.isHelpful ?? null;
        state.createdAt = data.createdAt || null;
        state.expiresAt = data.expiresAt || null;
        state.publishedAt = data.publishedAt || null;
        state.publishedAnswer = data.answer || null;
      })
      .addCase(fetchAIAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(askAI.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
        state.humanAnswerExists = false;
      })
      .addCase(askAI.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        state.status = data.status || "draft";
        state.aiAnswerId = data.aiAnswerId || null;
        state.aiAnswer = data.aiAnswer || null;
        state.isHelpful = data.isHelpful ?? null;
        state.createdAt = data.createdAt || null;
        state.expiresAt = data.expiresAt || null;
        state.publishedAt = data.publishedAt || null;
        state.publishedAnswer = data.answer || null;
        state.error = null;
        state.errorCode = null;
        state.humanAnswerExists = false;
      })
      .addCase(askAI.rejected, (state, action) => {
        state.loading = false;

        const payload = action.payload || {};
        state.error = payload.message || "Failed to generate AI answer";
        state.errorCode = payload.code || null;

        if (payload.code === "HUMAN_ANSWER_EXISTS") {
          state.humanAnswerExists = true;
        }
      })

      .addCase(submitAIFeedback.pending, (state) => {
        state.feedbackLoading = true;
        state.error = null;
      })
      .addCase(submitAIFeedback.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        state.isHelpful = action.payload.helpful;
        state.error = null;
      })
      .addCase(submitAIFeedback.rejected, (state, action) => {
        state.feedbackLoading = false;
        state.error = action.payload;
      })

      .addCase(refineAIAnswer.pending, (state) => {
        state.refining = true;
        state.error = null;
      })
      .addCase(refineAIAnswer.fulfilled, (state, action) => {
        state.refining = false;

        const data = action.payload || {};

        state.status = data.status || "draft";
        state.aiAnswerId = data.aiAnswerId || state.aiAnswerId;
        state.aiAnswer = data.aiAnswer || null;
        state.isHelpful = null;
        state.error = null;
      })
      .addCase(refineAIAnswer.rejected, (state, action) => {
        state.refining = false;
        state.error = action.payload;
      })

      .addCase(publishAIAnswer.pending, (state) => {
        state.publishing = true;
        state.error = null;
      })
      .addCase(publishAIAnswer.fulfilled, (state, action) => {
        state.publishing = false;
        state.status = "published";
        state.publishedAt = action.payload?.publishedAt || new Date().toISOString();
        state.publishedAnswer = action.payload?.answer || null;
        state.error = null;
      })
      .addCase(publishAIAnswer.rejected, (state, action) => {
        state.publishing = false;
        state.error = action.payload;
      })

      .addCase(discardAIAnswer.pending, (state) => {
        state.discarding = true;
        state.error = null;
      })
      .addCase(discardAIAnswer.fulfilled, (state) => {
        state.discarding = false;
        state.status = "none";
        state.aiAnswerId = null;
        state.aiAnswer = null;
        state.isHelpful = null;
        state.expiresAt = null;
        state.publishedAt = null;
        state.publishedAnswer = null;
        state.error = null;
      })
      .addCase(discardAIAnswer.rejected, (state, action) => {
        state.discarding = false;
        state.error = action.payload;
      });
  },
});

export const { clearAIAnswer, clearAIError } = aiAnswerSlice.actions;
export default aiAnswerSlice.reducer;
